/**
 * Universal Date & Timestamp Utilities
 * Guaranteed resilient against Firestore Timestamps, serialized objects, ISO strings, numbers, and nulls.
 * Prevents TypeError exceptions (e.g. `(createdAt || "").localeCompare is not a function`).
 */

/**
 * Extracts numeric timestamp in milliseconds from any date representation:
 * - number (ms or seconds)
 * - ISO string / RFC2822 date string
 * - Date instance
 * - Firestore Timestamp instance (.toMillis() or .toDate())
 * - Serialized Firestore Timestamp object ({ seconds, nanoseconds } or { _seconds, _nanoseconds })
 * - Returns 0 if invalid or empty
 */
export function getTimestampMs(val: any): number {
  if (val === null || val === undefined || val === '') return 0;

  // Direct number
  if (typeof val === 'number') {
    if (!Number.isFinite(val) || isNaN(val)) return 0;
    // If timestamp is in seconds (10 digits, e.g. < 10000000000), convert to ms
    if (val > 0 && val < 10000000000) {
      return val * 1000;
    }
    return val;
  }

  // Firestore Timestamp instance methods
  if (typeof val.toMillis === 'function') {
    try {
      const ms = val.toMillis();
      if (typeof ms === 'number' && !isNaN(ms)) return ms;
    } catch {}
  }

  if (typeof val.toDate === 'function') {
    try {
      const d = val.toDate();
      if (d instanceof Date && !isNaN(d.getTime())) return d.getTime();
    } catch {}
  }

  // Serialized Firestore object { seconds, nanoseconds }
  if (typeof val.seconds === 'number' && Number.isFinite(val.seconds)) {
    const nanos = typeof val.nanoseconds === 'number' && Number.isFinite(val.nanoseconds) ? val.nanoseconds : 0;
    return val.seconds * 1000 + Math.floor(nanos / 1000000);
  }

  // Serialized Firestore object { _seconds, _nanoseconds }
  if (typeof val._seconds === 'number' && Number.isFinite(val._seconds)) {
    const nanos = typeof val._nanoseconds === 'number' && Number.isFinite(val._nanoseconds) ? val._nanoseconds : 0;
    return val._seconds * 1000 + Math.floor(nanos / 1000000);
  }

  // Date instance
  if (val instanceof Date) {
    const ms = val.getTime();
    return isNaN(ms) ? 0 : ms;
  }

  // String date
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (!trimmed) return 0;
    const parsed = Date.parse(trimmed);
    if (!isNaN(parsed)) return parsed;

    // Check if string contains only digits (epoch ms or seconds)
    if (/^\d+$/.test(trimmed)) {
      const num = parseInt(trimmed, 10);
      if (num < 10000000000) return num * 1000;
      return num;
    }
  }

  return 0;
}

/**
 * Converts any date representation into a guaranteed ISO 8601 string.
 * Never returns an object.
 */
export function toSafeIsoString(val: any, fallback = ''): string {
  if (val === null || val === undefined || val === '') return fallback;

  // If already a valid ISO string, return trimmed
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(trimmed)) {
      return trimmed;
    }
  }

  const ms = getTimestampMs(val);
  if (ms > 0) {
    try {
      return new Date(ms).toISOString();
    } catch {}
  }

  return typeof val === 'string' ? val : fallback;
}

/**
 * Compare two timestamps for Array.prototype.sort().
 * Guaranteed NEVER to throw TypeError.
 * @param a First item date
 * @param b Second item date
 * @param direction 'asc' for oldest first, 'desc' for newest first (default)
 */
export function compareTimestamps(a: any, b: any, direction: 'asc' | 'desc' = 'desc'): number {
  const timeA = getTimestampMs(a);
  const timeB = getTimestampMs(b);

  if (direction === 'asc') {
    return timeA - timeB;
  }
  return timeB - timeA;
}

/**
 * Safely format a date for UI display without throwing or displaying "Invalid Date".
 */
export function formatSafeDate(
  val: any,
  locale = 'fr-FR',
  options?: Intl.DateTimeFormatOptions
): string {
  const ms = getTimestampMs(val);
  if (!ms) return '—';

  try {
    return new Date(ms).toLocaleDateString(locale, options);
  } catch {
    return '—';
  }
}

/**
 * Safely format a date and time for UI display without throwing or displaying "Invalid Date".
 */
export function formatSafeDateTime(
  val: any,
  locale = 'fr-FR',
  options?: Intl.DateTimeFormatOptions
): string {
  const ms = getTimestampMs(val);
  if (!ms) return '—';

  try {
    return new Date(ms).toLocaleString(locale, options);
  } catch {
    return '—';
  }
}
